// Module H · Global search palette client.
//
// Wraps rpc_admin_global_search with a small debounce + a deep-link
// resolver. Recent queries persist to localStorage (last 5).

import { useEffect, useMemo, useRef, useState } from "react";
import { supabase, isSupabaseConfigured } from "@/lib/supabase";

const RECENT_KEY = "globalSearch.recent.v1";
const RECENT_MAX = 5;

/** Fire one search RPC; returns the jsonb blob or null on RLS deny. */
export async function runGlobalSearch(query, { maxPerKind = 5 } = {}) {
  if (!isSupabaseConfigured) return null;
  const q = (query || "").trim();
  if (!q) return null;
  const { data, error } = await supabase.rpc("rpc_admin_global_search", {
    p_query: q,
    p_max_per_kind: maxPerKind,
  });
  if (error) {
    if (error.code === "PGRST202" || error.code === "42883") return null;
    throw error;
  }
  return data || null;
}

/** Map (kind, id) → routable path. v1 only links to surfaces that
 *  exist already; we never produce dead-end results. */
export function deepLinkFor(kind, item) {
  switch (kind) {
    case "families":     return `/admin/families/${item.id}`;
    case "children":     return `/admin/children`;         // list page (no per-child detail route yet)
    case "staff":        return `/admin/staff`;            // list page (no per-staff detail route yet)
    case "invoices":     return `/admin/billing`;          // detail lives inline on Billing
    case "incidents":    return `/admin/incidents/${item.id}`;
    case "inquiries":    return `/admin/inquiries/${item.id}`;
    case "pages":        return `/cms/pages/${item.id}`;
    case "announcements":return `/cms/announcements`;      // list page (no per-announcement detail route)
    default:             return null;
  }
}

export const KIND_META = {
  families:      { label: "Families",         icon: "Users" },
  children:      { label: "Children",         icon: "Baby" },
  staff:         { label: "Staff",            icon: "ChalkboardTeacher" },
  invoices:      { label: "Invoices",         icon: "Receipt" },
  incidents:     { label: "Incidents",        icon: "FirstAid" },
  inquiries:     { label: "Inquiries",        icon: "Flag" },
  pages:         { label: "CMS pages",        icon: "FileText" },
  announcements: { label: "Announcements",    icon: "Megaphone" },
};

export const KIND_ORDER = [
  "families", "children", "staff", "invoices",
  "incidents", "inquiries", "pages", "announcements",
];

// Recent searches ----------------------------------------------------

export function loadRecentSearches() {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.slice(0, RECENT_MAX) : [];
  } catch {
    return [];
  }
}

export function pushRecentSearch(q) {
  const trimmed = (q || "").trim();
  if (!trimmed) return;
  try {
    const prev = loadRecentSearches();
    const next = [trimmed, ...prev.filter((p) => p !== trimmed)].slice(0, RECENT_MAX);
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch {
    /* localStorage unavailable — non-fatal */
  }
}

// Debounced search hook ----------------------------------------------

export function useGlobalSearch(query, { delay = 200, maxPerKind = 5 } = {}) {
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const reqId = useRef(0);

  useEffect(() => {
    const q = (query || "").trim();
    if (!q) { setResults(null); setLoading(false); return; }
    const myReq = ++reqId.current;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const data = await runGlobalSearch(q, { maxPerKind });
        if (reqId.current === myReq) setResults(data);
      } catch {
        if (reqId.current === myReq) setResults(null);
      } finally {
        if (reqId.current === myReq) setLoading(false);
      }
    }, delay);
    return () => clearTimeout(t);
  }, [query, delay, maxPerKind]);

  return { results, loading };
}

/** Flatten the jsonb blob into a navigable result list (for ↑↓ keys).
 *  Honors KIND_ORDER and skips empty kinds. */
export function flattenResults(results) {
  if (!results) return [];
  const out = [];
  for (const kind of KIND_ORDER) {
    const items = Array.isArray(results[kind]) ? results[kind] : [];
    for (const item of items) {
      out.push({ kind, item, path: deepLinkFor(kind, item) });
    }
  }
  return out.filter((r) => r.path);
}

/** Hook: enables Cmd+K / Ctrl+K / "/" to open the palette globally. */
export function usePaletteHotkeys(onOpen) {
  useEffect(() => {
    function onKey(e) {
      const isModK = (e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K");
      const isSlash = e.key === "/" && !e.metaKey && !e.ctrlKey && !e.altKey;
      const tag = (e.target?.tagName || "").toLowerCase();
      const inEditable =
        tag === "input" || tag === "textarea" || e.target?.isContentEditable;
      if (isModK) {
        e.preventDefault();
        onOpen();
      } else if (isSlash && !inEditable) {
        e.preventDefault();
        onOpen();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onOpen]);
}

/** Memo helper for non-empty kind list (used by render). */
export function useNonEmptyKinds(results) {
  return useMemo(() => {
    if (!results) return [];
    return KIND_ORDER.filter((k) => Array.isArray(results[k]) && results[k].length > 0);
  }, [results]);
}
